// Leaderboard client for the walkit-style Pages Function + D1 backend.
//
// Server side stays where it is (walkit-portal/functions/api/leaderboard.js): best-per-name,
// top 25, optional per-route scoping. This is just the typed fetch client + an offline/local
// mirror (walkit's localBoard) so a game still shows a board with no network / no D1 yet.

export interface ScoreEntry {
  name: string;
  score: number;
  stars?: number;
  route?: string | null;
  time_secs?: number | null;
  date?: string;
}

export interface SubmitInput {
  name: string;
  score: number;
  stars?: number;
  route?: string | null;
  /** completion time in whole seconds (optional) */
  time?: number | null;
}

export interface LeaderboardClient {
  /** Fetch the board, optionally scoped to a route/level. Falls back to the local mirror on failure. */
  fetch(route?: string): Promise<ScoreEntry[]>;
  /** Submit a score. Always updates the local mirror; posts to the server when not in local mode. */
  submit(input: SubmitInput): Promise<ScoreEntry[]>;
  /** The current local mirror (best-per-name-per-route), sorted. */
  local(route?: string): ScoreEntry[];
}

export interface LeaderboardOptions {
  /** API endpoint. Default '/api/leaderboard'. */
  endpoint?: string;
  /** localStorage key for the offline mirror. Default 'gamekit_board'. */
  localKey?: string;
  /** Skip the network entirely and run purely off the local mirror (demo / offline). */
  localOnly?: boolean;
}

const keyOf = (e: { name?: string; route?: string | null }) =>
  (e.name || '').toLowerCase() + '|' + (e.route || '');
const timeKey = (e: ScoreEntry) => (e.time_secs != null ? e.time_secs : 1e9);

function sortBoard(b: ScoreEntry[]): ScoreEntry[] {
  return b.sort(
    (a, c) =>
      c.score - a.score ||
      timeKey(a) - timeKey(c) ||
      (String(a.date) < String(c.date) ? -1 : 1),
  );
}

export function createLeaderboard(opts: LeaderboardOptions = {}): LeaderboardClient {
  const endpoint = opts.endpoint ?? '/api/leaderboard';
  const localKey = opts.localKey ?? 'gamekit_board';
  const localOnly = opts.localOnly ?? false;

  const readLocal = (): ScoreEntry[] => {
    try {
      const raw = localStorage.getItem(localKey);
      const arr = raw ? (JSON.parse(raw) as ScoreEntry[]) : [];
      return Array.isArray(arr) ? arr : [];
    } catch {
      return [];
    }
  };
  const writeLocal = (b: ScoreEntry[]) => {
    try {
      localStorage.setItem(localKey, JSON.stringify(b.slice(0, 150)));
    } catch {
      /* ignore */
    }
  };

  // best-per-name-per-route upsert (walkit's localBoardUpsert)
  const upsertLocal = (entry: ScoreEntry) => {
    const b = readLocal();
    const i = b.findIndex((x) => keyOf(x) === keyOf(entry));
    if (i < 0) b.push(entry);
    else if (
      entry.score > b[i].score ||
      (entry.score === b[i].score && timeKey(entry) < timeKey(b[i]))
    )
      b[i] = entry;
    writeLocal(sortBoard(b));
  };

  const localFor = (route?: string): ScoreEntry[] =>
    sortBoard(
      readLocal().filter((e) => (route ? e.route === route : true)),
    ).slice(0, 25);

  return {
    local: localFor,

    async fetch(route?: string) {
      if (localOnly) return localFor(route);
      try {
        const url = route ? `${endpoint}?route=${encodeURIComponent(route)}` : endpoint;
        const res = await fetch(url, { headers: { accept: 'application/json' } });
        const data = (await res.json()) as { board?: ScoreEntry[] };
        return Array.isArray(data.board) ? data.board : localFor(route);
      } catch {
        return localFor(route); // offline — show the mirror
      }
    },

    async submit(input: SubmitInput) {
      const entry: ScoreEntry = {
        name: input.name,
        score: input.score,
        stars: input.stars ?? 0,
        route: input.route ?? null,
        time_secs: input.time ?? null,
        date: new Date().toISOString(),
      };
      upsertLocal(entry); // optimistic + offline safety net

      if (localOnly) return localFor(input.route ?? undefined);
      try {
        const res = await fetch(endpoint, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(input),
        });
        const data = (await res.json()) as { board?: ScoreEntry[] };
        return Array.isArray(data.board) ? data.board : localFor(input.route ?? undefined);
      } catch {
        return localFor(input.route ?? undefined);
      }
    },
  };
}
